import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "@/packages/utils";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class BillingErrorBoundaryComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Billing section error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <BillingErrorFallback />;
    }

    return this.props.children;
  }
}

function BillingErrorFallback() {
  const { t } = useTranslation(["settings"]);

  return (
    <div className="rounded-card border border-border p-8 bg-card text-center">
      <AlertTriangle size={32} className="mx-auto text-muted mb-3" />
      <h3 className="text-[16px] font-semibold text-primary mb-1">
        {t("settings:billing.errorTitle")}
      </h3>
      <p className="text-[13px] text-secondary">
        {t("settings:billing.errorMessage")}
      </p>
    </div>
  );
}

export { BillingErrorBoundaryComponent as BillingErrorBoundary };
